import { create } from "zustand"
import { persist } from "zustand/middleware"

export interface SocialLinks {
  twitter?: string
  discord?: string
  patreon?: string
  website?: string
  fansly?: string
  onlyfans?: string
  youtube?: string
  instagram?: string
  reddit?: string
  deviantart?: string
}

export interface UserProfile {
  userId: string
  username: string
  displayName: string
  email?: string
  avatar?: string
  banner?: string
  bio?: string
  socialLinks: SocialLinks
  isCreator: boolean
  isVerified: boolean
  trustLevel?: string | null
  createdAt: number
  followersCount: number
  followingCount: number
  scriptsCount: number
  collectionsCount: number
  badgeFrameUrl?: string
  activityRank?: string
  activityXp?: number
  discordUsername?: string
  creator_mode?: boolean
  creator_tags?: string[]
  specialty_tags?: string[]
  creator_relief_enabled?: boolean
  creator_relief_days?: string[]
  creator_relief_note?: string | null
  acceptingCommissions?: boolean
  commissionNotes?: string | null
  commissionSlots?: number | null
  activeCommissions?: number
}

export interface CollectionItem {
  id: string
  type: "script" | "content"
  addedAt: number
  isLicensed?: boolean // true if this is a purchased script
  licenseId?: string
}

export interface Collection {
  id: string
  userId: string
  name: string
  description: string
  coverImage?: string
  isPublic: boolean
  items: CollectionItem[]
  createdAt: number
  updatedAt: number
  viewCount: number
  likeCount: number
}

export interface UserPreferences {
  profileTabOrder: ("collections" | "scripts")[]
  defaultCollectionVisibility: "public" | "private"
  showPurchasedInPublicCollections: boolean // Protection: hide purchased scripts in public collections
}

interface UserProfileState {
  // Current user profile
  currentProfile: UserProfile | null
  preferences: UserPreferences

  // Collections
  collections: Collection[]

  // Following/followers
  following: string[] // userIds
  followedUsernames: string[] // display usernames for booru post matching

  // Actions - Profile
  setProfile: (profile: UserProfile | null) => void
  updateProfile: (updates: Partial<UserProfile>) => void
  updatePreferences: (updates: Partial<UserPreferences>) => void

  // Actions - Collections
  createCollection: (name: string, description: string, isPublic: boolean) => string
  updateCollection: (id: string, updates: Partial<Collection>) => void
  deleteCollection: (id: string) => void
  addToCollection: (collectionId: string, item: Omit<CollectionItem, "addedAt">) => void
  removeFromCollection: (collectionId: string, itemId: string) => void

  // Actions - Social
  followUser: (userId: string, username?: string) => void
  unfollowUser: (userId: string, username?: string) => void
  isFollowing: (userId: string) => boolean

  // Getters
  getCollectionById: (id: string) => Collection | undefined
  getPublicCollections: () => Collection[]
  getCollectionsForItem: (itemId: string) => Collection[]
}

export const useUserProfileStore = create<UserProfileState>()(
  persist(
    (set, get) => ({
      currentProfile: null,
      preferences: {
        profileTabOrder: ["scripts", "collections"],
        defaultCollectionVisibility: "private",
        showPurchasedInPublicCollections: false, // Protected by default
      },
      collections: [],
      following: [],
      followedUsernames: [],

      setProfile: (profile) => {
        set({ currentProfile: profile })
      },

      updateProfile: (updates) => {
        set((state) => ({
          currentProfile: state.currentProfile ? { ...state.currentProfile, ...updates } : null,
        }))
      },

      updatePreferences: (updates) => {
        set((state) => ({
          preferences: { ...state.preferences, ...updates },
        }))
      },

      createCollection: (name, description, isPublic) => {
        const id = `collection-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
        const now = Date.now()
        const newCollection: Collection = {
          id,
          userId: get().currentProfile?.userId || "anonymous",
          name,
          description,
          isPublic,
          items: [],
          createdAt: now,
          updatedAt: now,
          viewCount: 0,
          likeCount: 0,
        }
        set((state) => ({
          collections: [...state.collections, newCollection],
        }))
        return id
      },

      updateCollection: (id, updates) => {
        set((state) => ({
          collections: state.collections.map((c) => (c.id === id ? { ...c, ...updates, updatedAt: Date.now() } : c)),
        }))
      },

      deleteCollection: (id) => {
        set((state) => ({
          collections: state.collections.filter((c) => c.id !== id),
        }))
      },

      addToCollection: (collectionId, item) => {
        set((state) => ({
          collections: state.collections.map((c) => {
            if (c.id !== collectionId) return c
            // Prevent duplicates
            if (c.items.some((i) => i.id === item.id)) return c
            return {
              ...c,
              items: [...c.items, { ...item, addedAt: Date.now() }],
              updatedAt: Date.now(),
            }
          }),
        }))
      },

      removeFromCollection: (collectionId, itemId) => {
        set((state) => ({
          collections: state.collections.map((c) => {
            if (c.id !== collectionId) return c
            return {
              ...c,
              items: c.items.filter((i) => i.id !== itemId),
              updatedAt: Date.now(),
            }
          }),
        }))
      },

      followUser: (userId, username) => {
        set((state) => {
          if (state.following.includes(userId)) return state
          return {
            following: [...state.following, userId],
            followedUsernames: username && !state.followedUsernames.includes(username)
              ? [...state.followedUsernames, username]
              : state.followedUsernames,
          }
        })
        fetch(`/api/users/${userId}/follow`, { method: "POST" }).catch(() => {})
      },

      unfollowUser: (userId, username) => {
        set((state) => ({
          following: state.following.filter((id) => id !== userId),
          followedUsernames: username
            ? state.followedUsernames.filter((u) => u !== username)
            : state.followedUsernames,
        }))
        fetch(`/api/users/${userId}/follow`, { method: "DELETE" }).catch(() => {})
      },

      isFollowing: (userId) => {
        return get().following.includes(userId)
      },

      getCollectionById: (id) => {
        return get().collections.find((c) => c.id === id)
      },

      getPublicCollections: () => {
        return get().collections.filter((c) => c.isPublic)
      },

      getCollectionsForItem: (itemId) => {
        return get().collections.filter((c) => c.items.some((i) => i.id === itemId))
      },
    }),
    {
      name: "user-profile-storage",
    },
  ),
)
